import { useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { selectAllPreviousSurveyAnswers } from "store/reducers/survey/SurveySlice";
import {
  hasUserCompletedEnoughOfGettingStartedSurvey,
  flattenSurveyAnswers
} from "utils/v3/surveyUtils";
import {
  selectExistingOrdersWithoutCancelled,
  selectTestKitsGLIsCurrentlyOffering
} from "store/reducers/order/OrderInformationSlice";
import {
  doesMemberNeedToTalkToNavBasedOnFlattenedSurveyAnswers,
  figureOutKitsToOfferMemberRightNow
} from "utils/v3/testKitUtils";
import { Kit } from "types/kit";

import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import PageTitle from "utils/PageTitle";
import MembershipExpiredBanner from "components/v3/sharedComponents/MembershipExpiredBanner";

import GettingStartedHero from "components/v3/sharedComponents/WelcomeHero/GettingStartedHero";
import RequestTestsHero from "components/v3/sharedComponents/WelcomeHero/RequestTestsHero";
import TestingInProgressHero from "components/v3/sharedComponents/WelcomeHero/TestingInProgressHero";
import TestingNavigationTeamHero from "components/v3/sharedComponents/WelcomeHero/TestingNavigationTeamHero";
import CancerNavigationDashboardCtaCard from "components/v3/sharedComponents/DashboardCtaCard/CancerNavigationDashboardCtaCard";
import HealthSurveyDashboardCtaCard from "components/v3/sharedComponents/DashboardCtaCard/HealthSurveyDashboardCtaCard";
import InviteSpouseDashboardCtaCard from "components/v3/sharedComponents/DashboardCtaCard/InviteSpouseDashboardCtaCard";
import NeedSupportDashboardCtaCard from "components/v3/sharedComponents/DashboardCtaCard/NeedSupportDashboardCtaCard";
import InviteFriendsDashboardBar from "components/v3/sharedComponents/InviteFriendsDashboardBar";
import ProactiveHealthResources from "components/v3/sharedComponents/ProactiveHealthResources";
import CancerDashboardAlternateCtaCard from "components/v3/sharedComponents/DashboardAlternateCtaCard/CancerDashboardAlternateCtaCard";
import DashboardResults from "components/v3/sharedComponents/DashboardResults";
import { Box } from "@mui/material";

const DashboardPage = () => {
  const previousSurveyAnswers = useSelector(selectAllPreviousSurveyAnswers);
  const userInformation = useSelector(selectUserPersonalInformation);
  const kitsAlreadyOrdered = useSelector(selectExistingOrdersWithoutCancelled);
  const kitsGLCurrentlyOffers = useSelector(
    selectTestKitsGLIsCurrentlyOffering
  );
  const ordersForKitsThatAreSubmittedButNotCompleteYet =
    kitsAlreadyOrdered.filter((order: Kit) => {
      return order.documentIds?.length === 0;
    });
  const hasOrdersInProgress =
    ordersForKitsThatAreSubmittedButNotCompleteYet?.length > 0;
  const isMemberActive = userInformation.isMemberActive;
  const kitsToOfferMemberRightNow = figureOutKitsToOfferMemberRightNow(
    kitsGLCurrentlyOffers,
    kitsAlreadyOrdered,
    previousSurveyAnswers,
    "",
    userInformation.glProgramId
  );
  const hasKitsToOfferMemberRightNow = kitsToOfferMemberRightNow?.length > 0;
  const hasCompletedEnoughOfGettingStartedSurvey =
    hasUserCompletedEnoughOfGettingStartedSurvey(previousSurveyAnswers);
  const flattenedSurveyAnswers = flattenSurveyAnswers(previousSurveyAnswers);
  const memberNeedsToTalkToNav =
    doesMemberNeedToTalkToNavBasedOnFlattenedSurveyAnswers(
      flattenedSurveyAnswers
    ) && !kitsAlreadyOrdered.length;

  const getWelcomeHeroComponent = useCallback(() => {
    if (memberNeedsToTalkToNav) {
      return <TestingNavigationTeamHero />;
    }

    if (!hasCompletedEnoughOfGettingStartedSurvey) {
      return <GettingStartedHero />;
    }

    if (hasKitsToOfferMemberRightNow) {
      return <RequestTestsHero />;
    }

    return null;
  }, [
    hasCompletedEnoughOfGettingStartedSurvey,
    hasKitsToOfferMemberRightNow,
    isMemberActive,
    memberNeedsToTalkToNav
  ]);

  PageTitle("Home");

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingBottom: "32px",
          paddingTop: "40px",
          paddingX: {
            xs: 2,
            md: 3
          },
          gap: 4
        }}
      >
        {!isMemberActive && (
          <Box sx={{ paddingX: { xs: 0, md: 2 } }}>
            <MembershipExpiredBanner />
          </Box>
        )}
        {isMemberActive ? (
          <>
            <Box sx={{ paddingX: 2 }}>{getWelcomeHeroComponent()}</Box>

            {hasOrdersInProgress && (
              <Box sx={{ paddingX: 2 }}>
                <TestingInProgressHero />
              </Box>
            )}
            <InviteFriendsDashboardBar />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <InviteSpouseDashboardCtaCard />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2
                }}
              >
                {!hasCompletedEnoughOfGettingStartedSurvey &&
                  !memberNeedsToTalkToNav && <HealthSurveyDashboardCtaCard />}
                <CancerNavigationDashboardCtaCard />
                <NeedSupportDashboardCtaCard />
              </Box>
            </Box>
            <DashboardResults />
            <ProactiveHealthResources />
            <CancerDashboardAlternateCtaCard />
          </>
        ) : (
          <>
            <DashboardResults />
            <ProactiveHealthResources />
          </>
        )}
      </Box>
    </GradientFramedLayout>
  );
};

export default DashboardPage;
